/**
 * As medidas de retorno em grade: usada na faixa TOTAL e em cada funil.
 *
 * Decisoes nao obvias:
 *  1. E um `dl` proprio, e nao o `StatCell` do kit, porque o leitor de tela
 *     precisa do par termo/definicao, a mesma grade aparece em dois tamanhos
 *     e cada celula carrega a variacao contra o periodo anterior.
 *  2. Cor no numero so em "Liquido para voce" (status-good), que e o numero
 *     que o lojista procura primeiro; a comissao fica em tinta apagada: e a
 *     parte do Converte Facil, nao um "gasto" -- palavra que esta tela nunca
 *     usa (decisao do dono, 24/09/2026).
 *  3. Cada rotulo tem `title` com a definicao de negocio (o que entra, o que
 *     nao entra). E o tooltip nativo: sem biblioteca, funciona com mouse e
 *     aparece no `dt` para leitor de tela via o texto do proprio rotulo.
 *  4. `medidas` escolhe QUAIS celulas aparecem, na ordem pedida: a faixa
 *     TOTAL destaca o liquido em separado e mostra as outras cinco aqui.
 *  5. Contagens grandes saem compactas ("12,3 mil"); dinheiro nunca -- o
 *     lojista confere o centavo.
 */

import { Badge } from '../../../components/ui';
import { formatBRLFromCents, formatCountCompact, formatPercentFromBps, VALOR_AUSENTE } from '../../../lib/format';
import type { ReturnMetrics } from '../types';
import { Delta, type Sentido, type TipoDeMedida } from './Delta';

export type Medida = 'bruto' | 'comissao' | 'liquido' | 'vendas' | 'ticket' | 'taxa';

export const TODAS_AS_MEDIDAS: readonly Medida[] = ['bruto', 'comissao', 'liquido', 'vendas', 'ticket', 'taxa'];

type Tom = 'neutral' | 'muted' | 'good';

const VALOR: Readonly<Record<Tom, string>> = {
  neutral: 'text-ink',
  muted: 'text-ink-muted',
  good: 'text-status-good',
};

const COLUNAS: Readonly<Record<number, string>> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-5',
  6: 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-6',
};

const ROTULO = 'text-[10px] font-semibold uppercase tracking-wide text-ink-subtle';

interface Definicao {
  readonly rotulo: string;
  readonly tipo: TipoDeMedida;
  readonly tom: Tom;
  readonly sentido: Sentido;
  readonly explicacao: (feeBps: number | null) => string;
  readonly valor: (m: ReturnMetrics) => number;
  readonly texto: (m: ReturnMetrics) => string;
}

const DEFINICOES: Readonly<Record<Medida, Definicao>> = {
  bruto: {
    rotulo: 'Recuperado bruto',
    tipo: 'dinheiro',
    tom: 'neutral',
    sentido: 'maior_melhor',
    explicacao: () => 'Valor pago pelos leads recuperados: pagamentos aprovados em até 7 dias depois de um contato do motor, creditados ao último contato. Qualquer compra do lead conta, pelo valor pago.',
    valor: (m) => m.recoveredAmountCents,
    texto: (m) => formatBRLFromCents(m.recoveredAmountCents),
  },
  comissao: {
    rotulo: 'Comissão Converte Fácil',
    tipo: 'dinheiro',
    tom: 'muted',
    sentido: 'neutro',
    explicacao: (fee) =>
      `${fee === null ? 'Percentual contratado' : formatPercentFromBps(fee)} sobre cada venda recuperada, congelado no momento da venda. Chargeback reverte a comissão; reembolso não. Sem venda recuperada, não há comissão.`,
    valor: (m) => m.commissionCents,
    texto: (m) => formatBRLFromCents(m.commissionCents),
  },
  liquido: {
    rotulo: 'Líquido para você',
    tipo: 'dinheiro',
    tom: 'good',
    sentido: 'maior_melhor',
    explicacao: () => 'Recuperado bruto menos a comissão: o que fica com a empresa. Sem o motor seria zero, porque esses leads já tinham abandonado o checkout ou deixado a assinatura vencer.',
    valor: (m) => m.netAmountCents,
    texto: (m) => formatBRLFromCents(m.netAmountCents),
  },
  vendas: {
    rotulo: 'Vendas recuperadas',
    tipo: 'contagem',
    tom: 'neutral',
    sentido: 'maior_melhor',
    explicacao: () => 'Pagamentos aprovados atribuídos ao motor no período: um por pagamento, dentro de 7 dias após um contato.',
    valor: (m) => m.recoveredCount,
    texto: (m) => formatCountCompact(m.recoveredCount),
  },
  ticket: {
    rotulo: 'Ticket médio',
    tipo: 'dinheiro',
    tom: 'neutral',
    sentido: 'maior_melhor',
    explicacao: () => 'Recuperado bruto dividido pelas vendas recuperadas. Sem venda no período, não há ticket.',
    valor: (m) => m.averageTicketCents,
    texto: (m) => (m.recoveredCount === 0 ? VALOR_AUSENTE : formatBRLFromCents(m.averageTicketCents)),
  },
  taxa: {
    rotulo: 'Taxa de recuperação',
    tipo: 'bps',
    tom: 'neutral',
    sentido: 'maior_melhor',
    explicacao: () => 'Vendas recuperadas sobre quem entrou no funil no período: abandonou o checkout há mais de 30 min ou teve a assinatura vencida.',
    valor: (m) => m.recoveryRateBps,
    texto: (m) => (m.recoveredCount === 0 ? VALOR_AUSENTE : formatPercentFromBps(m.recoveryRateBps)),
  },
};

export function GradeMetricas({
  metricas,
  anterior,
  feeBps = null,
  medidas = TODAS_AS_MEDIDAS,
  compacta = false,
  className = '',
}: {
  readonly metricas: ReturnMetrics;
  /** Periodo anterior de mesmo tamanho; `null`/omitido = sem linha de variacao. */
  readonly anterior?: ReturnMetrics | null;
  /** Percentual contratado, para a explicacao da comissao. */
  readonly feeBps?: number | null;
  readonly medidas?: readonly Medida[];
  /** Numeros menores, para a linha de cada funil. */
  readonly compacta?: boolean;
  readonly className?: string;
}): JSX.Element {
  const semVenda = metricas.recoveredCount === 0;
  const tamanho = compacta ? 'text-base' : 'text-lg sm:text-xl';
  const colunas = COLUNAS[medidas.length] ?? COLUNAS[6] ?? 'grid-cols-2';
  const base = anterior ?? null;

  return (
    <dl className={`grid gap-x-4 gap-y-3 ${colunas} ${className}`}>
      {medidas.map((medida) => {
        const def = DEFINICOES[medida];
        // Ticket e taxa sem venda (agora ou antes) nao tem variacao honesta: a celula mostra o marcador e fica sem linha.
        const podeComparar = base !== null && !(medida === 'ticket' && (semVenda || base.recoveredCount === 0)) && !(medida === 'taxa' && semVenda);
        return (
          <div key={medida} className="flex min-w-0 flex-col gap-0.5">
            <dt className={`${ROTULO} cursor-help underline decoration-dotted decoration-line underline-offset-2`} title={def.explicacao(feeBps)}>
              {def.rotulo}
            </dt>
            <dd className="flex min-w-0 flex-col gap-0.5">
              {medida === 'taxa' ? (
                <span className="flex items-center">
                  <Badge tone={semVenda ? 'neutral' : 'good'}>{def.texto(metricas)}</Badge>
                </span>
              ) : (
                <span className={`truncate font-semibold tabular-nums ${tamanho} ${VALOR[def.tom]}`}>{def.texto(metricas)}</span>
              )}
              {podeComparar && base !== null ? <Delta atual={def.valor(metricas)} anterior={def.valor(base)} tipo={def.tipo} sentido={def.sentido} /> : null}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/** Mesma grade, em cinza pulsando, para a altura nao saltar quando o dado chega. */
export function GradeMetricasEsqueleto({ compacta = false, quantidade = 6 }: { readonly compacta?: boolean; readonly quantidade?: number }): JSX.Element {
  const colunas = COLUNAS[quantidade] ?? COLUNAS[6] ?? 'grid-cols-2';
  return (
    <div aria-hidden="true" className={`grid gap-x-4 gap-y-3 ${colunas}`}>
      {Array.from({ length: quantidade }, (_, i) => (
        <div key={i} className="flex animate-pulse flex-col gap-1.5">
          <div className="h-2.5 w-20 rounded bg-ink/10" />
          <div className={`${compacta ? 'h-5 w-24' : 'h-6 w-28'} rounded bg-ink/10`} />
          <div className="h-2.5 w-16 rounded bg-ink/10" />
        </div>
      ))}
    </div>
  );
}
